// src/features/system/developer/components/video-gallery/SidecarsBadge.tsx
// ────────────────────────────────────────────────────────────────────────────
// Operator-visibility badge for per-video sidecar lifecycle state.
//
// Surfaces the BFF `digestVideo()` sidecar digest on each card without
// poking GCS or the orchestrator:
//
//   sidecars_complete       → green ✓ chip
//   sidecars_pending_count  → amber "N…" chip (in-flight repair)
//   sidecars_failed_count   → rose "N!" chip (permanent_failed, operator attention)
//
// Steady-state cards (everything ok) render NOTHING — visual quiet is
// the dominant signal. Anything degraded gets a glanceable chip per
// PAC Glass dark-only conventions (no bg swap; border + tonal tint).
//
// The `title` attribute carries the per-kind breakdown so operators
// can hover to see exactly which kinds are pending / failed without
// opening a detail view.
// ────────────────────────────────────────────────────────────────────────────
import React, { memo } from 'react';
import type { VideoFeedItem } from './types';

interface Props {
  item: VideoFeedItem;
  /** Compact mode (smaller padding/font) for dense grids. */
  compact?: boolean;
}

function formatBreakdown(state: Record<string, string> | undefined): string {
  if (!state) return '';
  const groups: Record<string, string[]> = {};
  for (const [k, v] of Object.entries(state)) {
    if (!v || v === 'ok' || v === 'not_eligible') continue;
    (groups[v] ||= []).push(k);
  }
  const lines = Object.entries(groups).map(
    ([status, kinds]) => `${status}: ${kinds.join(', ')}`,
  );
  return lines.join(' • ');
}

function SidecarsBadgeImpl({ item, compact = false }: Props) {
  const pending = Number(item.sidecars_pending_count || 0);
  const failed  = Number(item.sidecars_failed_count  || 0);
  const complete = Boolean(item.sidecars_complete);

  // Steady state: render nothing (visual quiet wins).
  if (complete && pending === 0 && failed === 0) return null;

  const breakdown = formatBreakdown(item.sidecars_state as Record<string, string> | undefined);

  // Failed takes priority (operator action required).
  if (failed > 0) {
    return (
      <span
        className={[
          'inline-flex items-center gap-1 rounded font-mono uppercase tracking-wide backdrop-blur-sm',
          'bg-rose-600/15 text-rose-200 border border-rose-300/40',
          compact ? 'px-1.5 py-0.5 text-[9px]' : 'px-2 py-0.5 text-[10px]',
        ].join(' ')}
        title={breakdown ? `Sidecars: ${breakdown}` : `${failed} sidecar(s) failed`}
        aria-label={`${failed} sidecar(s) permanently failed`}
      >
        <span aria-hidden>!</span>
        {failed} failed
      </span>
    );
  }

  // Pending in-flight repair.
  if (pending > 0) {
    return (
      <span
        className={[
          'inline-flex items-center gap-1 rounded font-mono uppercase tracking-wide backdrop-blur-sm',
          'bg-amber-500/15 text-amber-200 border border-amber-300/40',
          compact ? 'px-1.5 py-0.5 text-[9px]' : 'px-2 py-0.5 text-[10px]',
        ].join(' ')}
        title={breakdown ? `Sidecars: ${breakdown}` : `${pending} sidecar(s) in progress`}
        aria-label={`${pending} sidecar(s) pending`}
      >
        <span aria-hidden>…</span>
        {pending} pending
      </span>
    );
  }

  // Incomplete without explicit pending/failed signals is non-actionable
  // noise for operators, so we suppress the generic "partial" chip.
  if (!complete) return null;

  return null;
}

export const SidecarsBadge = memo(SidecarsBadgeImpl);
export default SidecarsBadge;
